import React from 'react';
import _ from 'lodash';

import {
  fields,
  Form,
  LoadingOverlay,
  modalUtil,
  SectionHeaderBox,
  Table as AppModulesTable
} from 'iron-forge-react-modules';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Link,
  styled
} from '@mui/material';
import {
  activityTemplates,
  ApprovalStatusToggle,
  ApprovalTypeChip,
  ReceiveReturnAuthorizationModal,
  currencyUtil,
  selectFields,
  routeDirectory,
  appFormUtil
} from 'modules';
import {
  Autorenew,
  Check,
  Receipt
} from '@mui/icons-material';

import { useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import * as dataService from './dataService';
import * as types from './types';



const FormContainerBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
  '& form': {
    width: '100%'
  }
}));

const Table = styled('table')(({ theme }) => ({
  '& th': {
    padding: theme.spacing(1)
  },
  '& td': {
    padding: theme.spacing(1)
  }
}));

const FormContentBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
  width: '100%',
  alignItems: 'center'
}));

const TableComponent = AppModulesTable.genTableComponent<types.IColumnId>({});

const { genController } = selectFields.StoreSelectField.genComponentAndControllerGenerator();
const StoreSelectFieldController = genController<types.IFormState>();
const { genController: genAuthorizationApprovalStatusToggleController } = ApprovalStatusToggle.genComponentAndControllerGenerator();
const ControlledApprovalStatusToggle = genAuthorizationApprovalStatusToggleController<types.IFormState>();
const DateFieldController = fields.DateField.genControllerComponent<types.IFormState>();
const ControlledCheckbox = fields.CheckboxField.genControllerComponent<types.IFormState>();



export const ReturnAuthorizationsActivityDeprecated: React.FC = () => {
  const {
    pageInfo,
    fnQueryPage,
    queryTuple,
    fnQuery
  } = dataService.useQuery();
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers(['receiveItems']);
  const [selectedReturnAuthorizationId, setSelectedReturnAuthorizationId] = React.useState('');
  const form = useForm<types.IFormState>({
    defaultValues: {
      after: DateTime.now().minus({ days: 7 }).toJSDate(),
      before: DateTime.now().endOf('day').toJSDate(),
      authorizationApprovalStatus: [],
      acceptanceApprovalStatus: [],
      refundApprovalStatus: [],
      isReceived: false
    }
  });



  const handleClickReceiveItems = React.useCallback(
    (returnAuthorizationId: string) => {
      setSelectedReturnAuthorizationId(returnAuthorizationId);
      setModalIsOpen('receiveItems', true);
    },
    [setModalIsOpen]
  );

  function handleCloseReceiveReturnAuthorizationModal() {
    setSelectedReturnAuthorizationId('');
    setModalIsOpen('receiveItems', false);
    if (queryTuple.refetch) {
      queryTuple.refetch();
    }
  }

  function processSubmit(formState: types.IFormState) {
    fnQuery({
      filter: {
        formState: appFormUtil.trimFormStateValues(formState)
      }
    });
  }



  const rows = React.useMemo(
    () => _.map(queryTuple.data.returnAuthorizations, (auth): types.IRow => ({
      id: auth.id,
      expandedContent: (
        <Box>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Refund Amount</th>
              </tr>
            </thead>

            <tbody>
              {_.map(auth.lineItems, lineItem => (
                <tr key={lineItem.id}>
                  <td>
                    {lineItem.name}
                  </td>

                  <td>
                    {lineItem.sku}
                  </td>

                  <td>
                    {lineItem.quantity}
                  </td>

                  <td>
                    {currencyUtil.convertBigCentsToDollars(lineItem.refundAmountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Box>
      ),
      columnId2Value: {
        customerEmail: {
          type: 'string',
          label: auth.customerEmail
        },
        returnDate: {
          type: 'string',
          label: auth.returnDate.toFormat('yyyy-MM-dd @ hh:mm a')
        },
        returnNumber: {
          type: 'component',
          sortableValue: auth.returnNumber,
          label: (
            <Link
              href={routeDirectory.admin.returnAuthorizations.detail.path(auth.id)}
              target="_blank">
              {auth.returnNumber}
            </Link>
          )
        },
        approvalStatus: {
          type: 'component',
          sortableValue: auth.approvalStatus,
          label: (
            <ApprovalTypeChip.FC approvalTypeName={auth.approvalStatus} />
          )
        },
        returnLabel: {
          type: 'component',
          sortableValue: '',
          label: auth.returnLabelUrl ? (
            <Link
              href={auth.returnLabelUrl}
              target="_blank">
              {auth.returnTrackingNumber}
            </Link>
          ) : <Box />
        },
        shipmentStatus: {
          type: 'string',
          label: auth.shipmentStatus
        },
        isReceived: {
          type: 'component',
          sortableValue: auth.isReceived ? 'yes' : 'no',
          label: (auth.isReceived ?
            <Check color="success" /> : (
              <IconButton
                disabled={auth.approvalStatus !== 'approved'}
                onClick={_.partial(handleClickReceiveItems, auth.id)}>
                {auth.approvalStatus !== 'approved' ? <Autorenew /> : <Receipt />}
              </IconButton>
            )
          )
        },
        acceptanceStatus: {
          type: 'component',
          sortableValue: auth.acceptanceStatus,
          label: (
            <ApprovalTypeChip.FC approvalTypeName={auth.acceptanceStatus} />
          )
        },
        refundStatus: {
          type: 'component',
          sortableValue: auth.refundStatus,
          label: (
            <ApprovalTypeChip.FC approvalTypeName={auth.refundStatus} />
          )
        }
      }
    })),
    [
      handleClickReceiveItems,
      queryTuple.data.returnAuthorizations
    ]
  );

  return (
    <>
      <activityTemplates.AdminActivityTemplate.FC>
        <LoadingOverlay.FC active={queryTuple.loading} />

        <SectionHeaderBox.FC>
          <FormContainerBox>
            <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
              <FormContentBox>
                <StoreSelectFieldController
                  disableDefaultMargins
                  label="Store"
                  fieldKey="store"
                  formControl={form.control} />

                <DateFieldController
                  disableDefaultMargins
                  formControl={form.control}
                  label="Start"
                  fieldKey="after" />

                <DateFieldController
                  disableDefaultMargins
                  formControl={form.control}
                  label="End"
                  fieldKey="before" />

                <ControlledApprovalStatusToggle
                  label="Authorization Status"
                  formControl={form.control}
                  fieldKey="authorizationApprovalStatus" />

                <ControlledApprovalStatusToggle
                  label="Acceptance Status"
                  formControl={form.control}
                  fieldKey="acceptanceApprovalStatus" />

                <ControlledApprovalStatusToggle
                  label="Refund Status"
                  formControl={form.control}
                  fieldKey="refundApprovalStatus" />

                <ControlledCheckbox
                  label="Is Received"
                  formControl={form.control}
                  fieldKey="isReceived" />

                <Button
                  type="submit"
                  variant="contained">
                  Submit
                </Button>
              </FormContentBox>
            </Form.FC>
          </FormContainerBox>
        </SectionHeaderBox.FC>

        <Card>
          <CardContent>
            <TableComponent
              disableSorting
              paginationOptions={AppModulesTable.pageInfo2PaginationOptions(pageInfo, fnQueryPage)}
              rows={rows}
              initSortOptions={{
                columnId: 'returnDate',
                direction: 'descending'
              }}
              columns={[
                {
                  id: 'returnNumber',
                  label: "Return #"
                },
                {
                  id: 'returnDate',
                  label: "Return Date"
                },
                {
                  id: 'customerEmail',
                  label: "Customer Email"
                },
                {
                  id: 'approvalStatus',
                  label: "Authorization Approval"
                },
                {
                  id: 'returnLabel',
                  label: "Return Label"
                },
                {
                  id: 'shipmentStatus',
                  label: "Shipment Status"
                },
                {
                  id: 'isReceived',
                  label: "Is Received?"
                },
                {
                  id: 'acceptanceStatus',
                  label: "Return Accepted?"
                },
                {
                  id: 'refundStatus',
                  label: "Refund?"
                }
              ]} />
          </CardContent>
        </Card>
      </activityTemplates.AdminActivityTemplate.FC>

      {modalType2IsOpen.receiveItems && selectedReturnAuthorizationId && (
        <ReceiveReturnAuthorizationModal.FC
          returnAuthorizationId={selectedReturnAuthorizationId}
          onClose={handleCloseReceiveReturnAuthorizationModal} />
      )}
    </>
  );
};
